import { useCallback, useEffect, useRef, useState } from "react";
import { debounceCreator } from "helpers/utility/utilities";
import styles from "./style.module.scss";
import classNames from "classnames";

interface UniquenessSliderProps {
  handleUniquenessChange: (score: number) => void;
  score: number | null;
  onReset: () => void;
  uniquenessScoreLoading: boolean;
  removedElementsCount: number | null;
}

const min = 0;
const max = 1;

const UniquenessSlider = ({
  score,
  onReset,
  removedElementsCount,
  handleUniquenessChange,
  uniquenessScoreLoading,
}: UniquenessSliderProps) => {
  const [uniqueScore, setUniqueScore] = useState(score || min);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const sliderRef = useRef<HTMLInputElement>(null);

  const sliderWrapperClassnames = classNames(styles.sliderWrapper, {
    [styles.expandedWrapper]: score !== null,
  });

  const handleSliderChange = (event: any) => {
    setUniqueScore(parseFloat(event.target.value));
  };

  const handleMouseUp = () => {
    if (uniqueScore !== score) {
      handleUniquenessChange(uniqueScore);
    }
  };

  // Memoize the debounced function to prevent it from being recreated on each render
  // eslint-disable-next-line
  const debouncedHandleKeyDown = useCallback(
    debounceCreator((e: any) => {
      const includesArrowKeys = [
        "ArrowUp",
        "ArrowDown",
        "ArrowLeft",
        "ArrowRight",
      ].includes(e.key);

      if (!includesArrowKeys) {
        return;
      }

      const newVal = Number(e.target.value);
      if (score !== newVal) {
        handleUniquenessChange(newVal);
      }
    }, 700),
    []
  );

  // Update tooltip position dynamically whenever the slider value changes
  useEffect(() => {
    if (sliderRef.current && tooltipRef.current) {
      const slider = sliderRef.current;
      const tooltip = tooltipRef.current;

      const sliderWidth = slider.offsetWidth;
      const tooltipWidth = tooltip.offsetWidth;
      const percentage = (uniqueScore - min) / (max - min);
      const leftPosition = percentage * sliderWidth - tooltipWidth / 2;

      tooltip.style.left = `${leftPosition}px`;

      // Update the slider background dynamically to show the blue line from the thumb to the right end
      const filledPercentage = ((uniqueScore - min) / (max - min)) * 100;
      slider.style.background = `linear-gradient(to right, #00b6ff ${filledPercentage}%, #ddd ${filledPercentage}%)`;
    }
  }, [uniqueScore]);

  return (
    <div className={sliderWrapperClassnames}>
      {score !== null && (
        <div onClick={onReset} className={styles.resetButton}>
          Reset
        </div>
      )}

      <div className={styles.sliderContainer}>
        <div className={styles.sliderText}>Low</div>
        <div className={styles.inputContainer}>
          <input
            min={min}
            max={max}
            step={0.01}
            type="range"
            ref={sliderRef}
            value={uniqueScore}
            onMouseUp={handleMouseUp}
            className={styles.slider}
            onChange={handleSliderChange}
            onKeyDown={debouncedHandleKeyDown}
          />

          {uniqueScore !== null && (
            <div ref={tooltipRef} className={styles.tooltip}>
              {uniqueScore}
            </div>
          )}
        </div>

        <div className={styles.sliderText}>High</div>
      </div>
      {!uniquenessScoreLoading && score !== null && removedElementsCount && (
        <div
          className={styles.information}
        >{`Removed ${removedElementsCount} Images`}</div>
      )}
    </div>
  );
};

export default UniquenessSlider;
